import { Entity, System } from "ecsy";
import { ConditionalEffect, HasConditionalEffect, MergableComponent } from "../components";
import { getId } from "../entityFactory";
import { Game } from "../game";
import { selectAndFilter } from "../triggeredEffects";
import { addComponent, deepCopy, FrozenComponent, getOrAddComponent } from "../utils";

export class ConditionalEffectSystem extends System {
	game: Game;
	activeConditionalEffects = new Map<ConditionalEffect, Set<Entity>>();

	constructor(world: any, attributes: any) {
		// @ts-ignore
		super(world, attributes);
		this.game = attributes.game;
	}

	execute() {
		//First process all the effects, then do all removals followed by all adds
		const effectsToRemove = new Array<{ entity: Entity, component: FrozenComponent }>();
		const effectsToAdd = new Array<{ entity: Entity, component: FrozenComponent }>();

		for (const entity of this.queries.conditionalEffects.results) {
			const conditionalEffect = entity.getComponent(ConditionalEffect);

			if (!this.activeConditionalEffects.has(conditionalEffect)) {
				this.activeConditionalEffects.set(conditionalEffect, new Set());
			}
			const lastFrameMetCondition = this.activeConditionalEffects.get(conditionalEffect)!;

			const thisFrameMetCondition = new Set<Entity>();

			//This part does the actual check for what meets the condition
			const selector = conditionalEffect.targetSelector;
			const filters = conditionalEffect.filters;
			const meetsCondition = selectAndFilter(selector, filters, entity, this.game);

			for (const target of meetsCondition) {
				const comp = conditionalEffect.effect.component;
				//If the target already has the component don't add it unless its mergable..
				// const existingComp = target.getComponent(comp) as unknown as MergableComponent<typeof comp>;;
				// if (existingComp && !existingComp.merge) {
				// 	//..and keep checking don't mark it as added
				// 	continue;
				// }

				//Now we're just trying sorting out when we have added the component already from when we haven't
				thisFrameMetCondition.add(target);
				if (!lastFrameMetCondition.has(target)) {
					effectsToAdd.push({ entity: target, component: conditionalEffect.effect });
				}
				else {
					if (!target.hasComponent(comp)) {
						//If we statified the conditon last frame but the component isn't there then 
						//the component was removed by something else, and should be re-added
						effectsToAdd.push({ entity: target, component: conditionalEffect.effect });
					}
					lastFrameMetCondition.delete(target);
				}
			}

			const failedConditionThisFrame = Array.from(lastFrameMetCondition);
			for (const fail of failedConditionThisFrame) {
				effectsToRemove.push({ entity: fail, component: conditionalEffect.effect });
			}

			this.activeConditionalEffects.set(conditionalEffect, thisFrameMetCondition);
		}

		//Do adds and removals
		for (const remove of effectsToRemove) {
			if (getId(remove.entity) === null) continue;
			this.removeConditionalEffectIfPossible(remove.entity, remove.component);
		}

		for (const add of effectsToAdd) {
			this.addConditionalEffectIfPossible(add.entity, add.component);
		}

		//Remove any components this now removed conditionalEffect was maintaining
		this.queries.conditionalEffects.removed?.forEach(entity => {
			const conditionalEffect = entity.getRemovedComponent(ConditionalEffect);
			const entitiesThatMetCondition = this.activeConditionalEffects.get(conditionalEffect);
			if (!entitiesThatMetCondition) return;
			const entitiesToRemoveFrom = Array.from(entitiesThatMetCondition);

			for (const thing of entitiesToRemoveFrom) {
				this.removeConditionalEffectIfPossible(thing, conditionalEffect.effect);
			}

			this.activeConditionalEffects.delete(conditionalEffect);
		});
	}

	addConditionalEffectIfPossible(entity: Entity, frozenComponent: FrozenComponent) {
		//Only add a component if it either isn't there already, or if the one present was added by another conditional effect
		const hasCondEffectAlready = entity.getComponent(HasConditionalEffect)
		const hasComponent = entity.hasComponent(frozenComponent.component);
		const hasAnotherAdded = hasCondEffectAlready && hasCondEffectAlready.effects.some(effect => effect === frozenComponent.component);

		if (!hasComponent || hasAnotherAdded) {
			const values = frozenComponent.needsDeepCopy ? deepCopy(frozenComponent.values) : frozenComponent.values;
			addComponent(entity, frozenComponent.component, values);
			const hasCondEffect = getOrAddComponent(entity, HasConditionalEffect);
			hasCondEffect.effects.push(frozenComponent.component);
		}
	}

	removeConditionalEffectIfPossible(entity: Entity, frozenComponent: FrozenComponent) {
		//Only remove a component that was added by some conditional effect
		const hasConditional = entity.getMutableComponent(HasConditionalEffect) || entity.getRemovedComponent(HasConditionalEffect);
		if (!hasConditional) return;
		const wasAddedAsCondEffect = hasConditional.effects.some(effect => effect === frozenComponent.component);

		if (wasAddedAsCondEffect) {
			entity.removeComponent(frozenComponent.component);
			hasConditional.effects = hasConditional.effects.filter(effect => effect !== frozenComponent.component);
			if (hasConditional.effects.length === 0) entity.removeComponent(HasConditionalEffect)
		}
	}
}



ConditionalEffectSystem.queries = {
	conditionalEffects: { components: [ConditionalEffect], listen: { removed: true } },
}